/**
 * Fetching a world's files (ADR-032): each one as it comes, applied the
 * moment it arrives, and a lesson that waits for them at most 15 s. What
 * arrives later goes in at the next fade, unseen; what never arrives
 * (offline, never cached) leaves its fallback in place -- a texture its
 * average colour, a sky a gradient, a prop left out -- and never blocks.
 */

export const WORLD_WAIT_MS = 15_000;
/**
 * How long the rest waits for the file it waits for (requestFirst) at most:
 * a minute -- four times a lesson's own wait -- and the file is taken as
 * stuck, and the rest goes in without it.
 */
export const WORLD_FIRST_WAIT_MS = 60_000;

type Pending = { apply: () => void; first: boolean };

export class WorldLoader {
  readonly #cache = new Map<string, Promise<unknown>>();
  readonly #late: Pending[] = [];
  readonly #waiting = new Set<Promise<unknown>>();
  readonly #capMs: number;
  #capped = false;
  #loaded = 0;
  #missing = new Set<string>();
  #disposed = false;
  /** Settles once the file asked for with requestFirst has gone in, failed or was waited for long enough. */
  #gate: Promise<void> | undefined;
  #gateTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(options: { capMs?: number } = {}) {
    this.#capMs = options.capMs ?? WORLD_WAIT_MS;
  }

  /**
   * Asks for a file once (by its stored name), however many surfaces use it;
   * `apply` gets it when it arrives, or `undefined` when it cannot be had.
   * After requestFirst, it is put in only once that file has been.
   */
  request<T>(key: string, load: () => Promise<T>, apply: (value: T | undefined) => void): void {
    void this.#request(key, load, apply, false);
  }

  /**
   * Asks for the file every other waits for -- a world's sky, whose light is
   * made with little else on the GPU beside it (worlds/budget.ts, moments) --
   * before anything else: nothing asked for after it is put in until it has
   * been, or cannot be had, or WORLD_FIRST_WAIT_MS have gone. Late, it goes
   * in first at the fade.
   */
  requestFirst<T>(
    key: string,
    load: () => Promise<T>,
    apply: (value: T | undefined) => void,
  ): void {
    const settled = this.#request(key, load, apply, true);
    const stuck = new Promise<void>((resolve) => {
      this.#gateTimer = setTimeout(resolve, WORLD_FIRST_WAIT_MS);
    });
    this.#gate = Promise.race([settled, stuck]).then(() => {
      clearTimeout(this.#gateTimer);
      this.#gateTimer = undefined;
    });
  }

  #request<T>(
    key: string,
    load: () => Promise<T>,
    apply: (value: T | undefined) => void,
    first: boolean,
  ): Promise<void> {
    let promise = this.#cache.get(key) as Promise<T | undefined> | undefined;
    if (!promise) {
      promise = load().then(
        (value) => {
          this.#loaded++;
          return value;
        },
        (error: unknown) => {
          console.warn(`world file ${key} not loaded`, error);
          this.#missing.add(key);
          return undefined;
        },
      );
      this.#cache.set(key, promise);
    }
    const gate = first ? undefined : this.#gate;
    const arrived = gate ? Promise.all([promise, gate]).then(([value]) => value) : promise;
    const settled = arrived.then((value) => {
      if (this.#disposed) return;
      if (value === undefined) {
        apply(undefined);
        return;
      }
      if (this.#capped) this.#late.push({ apply: () => apply(value), first });
      else apply(value);
    });
    this.#waiting.add(settled);
    return settled;
  }

  /**
   * Settles when every file asked for so far has arrived or failed, or when
   * the wait is over, whichever is first. Files still coming after that are late.
   */
  ready(): Promise<void> {
    const all = Promise.all([...this.#waiting]).then(() => undefined);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const cap = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, this.#capMs);
    });
    return Promise.race([all, cap]).then(() => {
      if (timer !== undefined) clearTimeout(timer);
      this.#capped = true;
    });
  }

  /** Puts in what arrived after the wait; called at the black of a fade. How many went in. */
  applyLate(): number {
    const late = this.#late.splice(0);
    // The first file first, whenever the rest arrived.
    for (const pending of late) if (pending.first) pending.apply();
    for (const pending of late) if (!pending.first) pending.apply();
    return late.length;
  }

  /** Files that could not be had. */
  get missing(): number {
    return this.#missing.size;
  }

  get loaded(): number {
    return this.#loaded;
  }

  /** The lesson is over: nothing more is applied. */
  dispose(): void {
    this.#disposed = true;
    this.#late.length = 0;
    clearTimeout(this.#gateTimer);
  }
}
